import { useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRight, BrainCircuit, Check, ChevronRight, Flame, Layers3, MessageSquareText,
  MousePointerClick, Network, Play, Rocket, ShieldCheck, Sparkles, Target,
  WandSparkles, Workflow, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import appLogo from '@/assets/brand/cavernicolapp-flame.svg';
import creativeLogo from '@/assets/brand/cavernicola-creativo-flame.svg';
import './CavernicolAppLanding.css';

const WEBHOOK_URL = process.env.REACT_APP_CAVERNICOLAPP_LANDING_WEBHOOK || '';

const reveal = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.72, ease: [0.22, 1, 0.36, 1] } },
};

const modules = [
  { icon: MessageSquareText, title: 'Conversaciones que no se enfrían', text: 'Centraliza WhatsApp, redes y canales digitales para responder con contexto, velocidad y seguimiento real.' },
  { icon: BrainCircuit, title: 'IA que trabaja, no que adorna', text: 'Agentes entrenados con tu negocio para orientar, calificar, agendar y activar el siguiente paso comercial.' },
  { icon: Target, title: 'CRM que empuja la venta', text: 'Pipeline, tareas, agenda y trazabilidad para que cada lead tenga dueño, prioridad y una siguiente acción.' },
  { icon: Workflow, title: 'Automatizaciones con intención', text: 'Diseña recorridos que reaccionan al comportamiento del cliente y eliminan tareas repetitivas del equipo.' },
  { icon: Layers3, title: 'Marketing conectado a operación', text: 'Campañas, audiencias, captación y seguimiento dentro del mismo ecosistema, sin brincar entre diez pestañas.' },
  { icon: Network, title: 'Arquitectura que evoluciona contigo', text: 'Multiempresa, modular, white-label y lista para crecer por capas sin reconstruir todo cada vez.' },
];

const steps = [
  ['01', 'Conecta', 'Integramos tus fuentes de leads, canales, equipo y reglas comerciales.'],
  ['02', 'Entrena', 'La IA aprende tu oferta, procesos, objeciones y forma correcta de atender.'],
  ['03', 'Activa', 'Automatizas respuestas, asignaciones, seguimientos, agenda y campañas.'],
  ['04', 'Evoluciona', 'Mides qué convierte, corriges fricción y agregas módulos conforme crece la operación.'],
];

const painPoints = [
  'Leads que llegan y nadie atiende a tiempo',
  'Vendedores trabajando cada uno a su manera',
  'Información repartida entre chats, hojas y memoria',
  'Campañas sin trazabilidad hasta la venta',
];

const outcomes = [
  'Atención inmediata con contexto',
  'Seguimiento uniforme y medible',
  'Automatización sin perder el toque humano',
  'Decisiones basadas en señales reales',
];

export default function CavernicolAppLanding() {
  const reduceMotion = useReducedMotion();
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', challenge: '' });

  const motionProps = useMemo(() => reduceMotion ? {} : {
    initial: 'hidden', whileInView: 'visible', viewport: { once: true, margin: '-80px' }, variants: reveal,
  }, [reduceMotion]);

  const setField = (event) => setForm((current) => ({ ...current, [event.target.name]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error('Completa nombre, empresa y correo para continuar.');
      return;
    }

    if (!WEBHOOK_URL) {
      toast.success('Formulario listo. Falta conectar el webhook definitivo.');
      return;
    }

    try {
      setSending(true);
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, source: 'cavernicolapp-landing', createdAt: new Date().toISOString() }),
      });
      if (!response.ok) throw new Error('Webhook request failed');
      toast.success('Recibimos tus datos. Encendamos la evolución.');
      setForm({ name: '', company: '', email: '', phone: '', challenge: '' });
    } catch {
      toast.error('No pudimos enviar el formulario. Intenta nuevamente.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="cav-landing" data-testid="cavernicolapp-landing">
      <div className="cav-noise" aria-hidden="true" />
      <div className="cav-orb cav-orb-one" aria-hidden="true" />
      <div className="cav-orb cav-orb-two" aria-hidden="true" />

      <header className="cav-header">
        <a className="cav-brand" href="#inicio" aria-label="CavernicolApp inicio">
          <img src={appLogo} alt="" />
          <span>Cavernicol<strong>App</strong></span>
        </a>
        <nav className="cav-nav" aria-label="Navegación de landing">
          <a href="#plataforma">Plataforma</a>
          <a href="#metodo">Método</a>
          <a href="#evolucion">Evolución</a>
        </nav>
        <a className="cav-btn cav-btn-small" href="#contacto">Quiero evolucionar <ArrowRight /></a>
      </header>

      <main>
        <section id="inicio" className="cav-hero">
          <motion.div className="cav-hero-copy" {...motionProps}>
            <div className="cav-eyebrow"><Sparkles /> CRM + CEM + Marketing + IA en un solo ecosistema</div>
            <h1>Tu operación comercial no necesita más herramientas. <em>Necesita evolucionar.</em></h1>
            <p className="cav-lead">CavernicolApp conecta atención, ventas, marketing y agentes de inteligencia artificial para convertir el caos operativo en una experiencia que responde, aprende y vende.</p>
            <div className="cav-actions">
              <a className="cav-btn cav-btn-primary" href="#contacto">Diseñar mi evolución <ArrowRight /></a>
              <a className="cav-btn cav-btn-ghost" href="#plataforma"><Play /> Ver cómo funciona</a>
            </div>
            <div className="cav-trust-row">
              <span><ShieldCheck /> Arquitectura modular</span>
              <span><Zap /> Automatización inteligente</span>
              <span><WandSparkles /> Experiencia white-label</span>
            </div>
          </motion.div>

          <motion.div className="cav-hero-visual" initial={reduceMotion ? false : { opacity: 0, scale: 0.92, rotate: 2 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
            <div className="cav-command-card">
              <div className="cav-command-top">
                <span className="cav-status-dot" /> Operación en vivo
                <span className="cav-live-pill">IA activa</span>
              </div>
              <div className="cav-metric-grid">
                <div><small>Lead nuevo</small><strong>00:04</strong><span>Tiempo de respuesta</span></div>
                <div><small>Intención</small><strong>Alta</strong><span>Solicita demo</span></div>
                <div><small>Siguiente acción</small><strong>Agenda</strong><span>Hoy · 17:30</span></div>
              </div>
              <div className="cav-chat-flow">
                <div className="cav-bubble cav-bubble-lead">Necesito ordenar mis leads y no perder oportunidades.</div>
                <div className="cav-bubble cav-bubble-ai"><img src={appLogo} alt="" /> Entiendo. ¿Hoy tus leads llegan de WhatsApp, campañas o ambos?</div>
                <div className="cav-action-chip"><MousePointerClick /> Lead calificado · ruta comercial activada</div>
              </div>
            </div>
            <motion.div className="cav-floating-card cav-float-one" animate={reduceMotion ? {} : { y: [0, -10, 0] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}><Flame /> Respuesta inmediata</motion.div>
            <motion.div className="cav-floating-card cav-float-two" animate={reduceMotion ? {} : { y: [0, 12, 0] }} transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}><Rocket /> Más acción. Menos fricción.</motion.div>
          </motion.div>
        </section>

        <section className="cav-marquee" aria-label="Capacidades principales">
          <div><span>CRM inteligente</span><i>✦</i><span>Experiencia del cliente</span><i>✦</i><span>Agentes de IA</span><i>✦</i><span>Automatización</span><i>✦</i><span>Marketing omnicanal</span><i>✦</i><span>Analítica operativa</span><i>✦</i></div>
        </section>

        <section id="plataforma" className="cav-section cav-platform">
          <motion.div className="cav-section-heading" {...motionProps}>
            <span className="cav-kicker">Una sola médula operativa</span>
            <h2>Todo conectado para que el cliente sienta que tu empresa <em>sí sabe lo que hace.</em></h2>
            <p>No vendemos humo con dashboard bonito. Diseñamos una operación donde cada interacción deja contexto, dispara acciones y mejora la siguiente conversación.</p>
          </motion.div>
          <div className="cav-module-grid">
            {modules.map((module, index) => (
              <motion.article key={module.title} className="cav-module-card" {...motionProps} transition={{ delay: index * 0.07 }}>
                <div className="cav-icon-wrap"><module.icon /></div>
                <h3>{module.title}</h3>
                <p>{module.text}</p>
                <span>Explorar capacidad <ChevronRight /></span>
              </motion.article>
            ))}
          </div>
        </section>

        <section className="cav-section cav-before-after">
          <motion.div className="cav-ba-copy" {...motionProps}>
            <span className="cav-kicker">Del modo supervivencia al modo evolución</span>
            <h2>La tecnología correcta no complica. <em>Hace que todo por fin tenga sentido.</em></h2>
            <p>CavernicolApp se adapta a tu modelo comercial, tu equipo y tus etapas de crecimiento. Empiezas con lo que hoy urge y evolucionas sin tirar lo que ya construiste.</p>
          </motion.div>
          <div className="cav-ba-grid">
            <motion.div className="cav-ba-card cav-ba-dark" {...motionProps}>
              <small>ANTES</small><h3>Operación reactiva</h3>
              {painPoints.map((item) => <div key={item}><span>×</span>{item}</div>)}
            </motion.div>
            <motion.div className="cav-ba-card cav-ba-light" {...motionProps}>
              <small>CON CAVERNICOLAPP</small><h3>Operación inteligente</h3>
              {outcomes.map((item) => <div key={item}><Check />{item}</div>)}
            </motion.div>
          </div>
        </section>

        <section id="metodo" className="cav-section cav-method">
          <motion.div className="cav-section-heading cav-heading-light" {...motionProps}>
            <span className="cav-kicker">El método de la tribu</span>
            <h2>No digitalizamos el desorden. <em>Rediseñamos la experiencia.</em></h2>
          </motion.div>
          <div className="cav-step-grid">
            {steps.map(([number, title, text], index) => (
              <motion.article key={number} className="cav-step" {...motionProps} transition={{ delay: index * 0.09 }}>
                <span>{number}</span><h3>{title}</h3><p>{text}</p>
              </motion.article>
            ))}
          </div>
        </section>

        <section id="evolucion" className="cav-section cav-evolution">
          <motion.div className="cav-evolution-panel" {...motionProps}>
            <div className="cav-evolution-copy">
              <span className="cav-kicker">Diseñado para crecer por capas</span>
              <h2>Empieza donde más duele. Evoluciona hasta donde quieras llegar.</h2>
              <p>CRM, agenda, campañas, conversaciones, IA, integraciones, reportes y operación multiempresa. Activa únicamente lo que genera valor hoy y expande cuando el negocio lo pida.</p>
              <a href="#contacto" className="cav-btn cav-btn-primary">Construir mi ruta <ArrowRight /></a>
            </div>
            <div className="cav-layer-stack" aria-label="Capas de CavernicolApp">
              {['Experiencia del cliente', 'Inteligencia artificial', 'Automatización', 'Datos y trazabilidad', 'Operación comercial'].map((layer, index) => (
                <motion.div key={layer} animate={reduceMotion ? {} : { x: [0, index % 2 ? 7 : -7, 0] }} transition={{ duration: 4 + index, repeat: Infinity, ease: 'easeInOut' }}><span>{index + 1}</span>{layer}</motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        <section id="contacto" className="cav-section cav-contact">
          <motion.div className="cav-contact-copy" {...motionProps}>
            <div className="cav-brand-pair">
              <img src={creativeLogo} alt="Símbolo Cavernícola Creativo" />
              <span>Una evolución creada por<br/><strong>Cavernícola Creativo</strong></span>
            </div>
            <span className="cav-kicker">Hablemos de tu operación real</span>
            <h2>La siguiente gran evolución de tu empresa puede empezar con una conversación.</h2>
            <p>Cuéntanos qué está frenando hoy tu atención, seguimiento o crecimiento. Diseñaremos una ruta concreta, sin venderte módulos que todavía no necesitas.</p>
          </motion.div>

          <motion.form className="cav-form" onSubmit={handleSubmit} {...motionProps}>
            <div className="cav-form-row"><label>Nombre<input name="name" value={form.name} onChange={setField} placeholder="Tu nombre" /></label><label>Empresa<input name="company" value={form.company} onChange={setField} placeholder="Nombre de tu empresa" /></label></div>
            <div className="cav-form-row"><label>Correo<input type="email" name="email" value={form.email} onChange={setField} placeholder="tu@empresa.com" /></label><label>Teléfono<input name="phone" value={form.phone} onChange={setField} placeholder="55 0000 0000" /></label></div>
            <label>¿Qué necesitas evolucionar primero?<textarea name="challenge" value={form.challenge} onChange={setField} rows="4" placeholder="Ej. Atención de leads, seguimiento comercial, automatización, IA..." /></label>
            <button className="cav-btn cav-btn-primary cav-submit" disabled={sending}>{sending ? 'Enviando...' : 'Quiero diseñar mi evolución'} <ArrowRight /></button>
            <small>Tu información se usará únicamente para contactar contigo sobre CavernicolApp.</small>
          </motion.form>
        </section>
      </main>
    </div>
  );
}
